import { motion } from 'framer-motion';
import { 
  Leaf, Zap, Bike, Globe, Target, Heart, 
  ExternalLink, CheckCircle, TreePine 
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { CTASection } from '@/components/sections/CTASection';
import { FaqSection } from '@/components/sections/FaqSection';
import { getBreadcrumbJsonLd, getFaqJsonLd, BASE_URL } from '@/lib/seo-constants';
import { useT, useLang } from '@/lib/i18n';
import sustainabilityImage from '@/assets/sustainability-new.webp';

export default function NachhaltigkeitPage() {
  const t = useT();
  const lang = useLang();
  const initiatives = [
    { icon: Zap, title: t('Ökostrom-Produktion', 'Green electricity production'), description: t('Wir produzieren mit 100% Ökostrom aus Wasserkraft von Lichtblick. Auch unser Erdgas ist ökologisch zertifiziert.', 'We produce with 100% renewable electricity from hydropower (Lichtblick). Our natural gas is ecologically certified as well.'), status: t('Umgesetzt', 'Implemented') },
    { icon: Bike, title: t('Lieferung per Lastenrad', 'Cargo-bike delivery'), description: t('Regionale Kunden beliefern wir CO₂-frei mit unserem Lastenrad. Größere Teile kommen per Erdgas-Lieferwagen.', 'Regional customers are served CO₂-free by cargo bike. Larger parts ship in our natural-gas delivery van.'), status: t('Umgesetzt', 'Implemented') },
    { icon: Globe, title: t('BNW-Mitgliedschaft', 'BNW membership'), description: t('Seit 2020 sind wir Mitglied im Bundesverband Nachhaltige Wirtschaft und setzen uns für eine grünere Wirtschaft ein.', 'Since 2020 we are a member of the German Federal Association for Sustainable Economy and advocate for a greener economy.'), status: t('Seit 2020', 'Since 2020') },
    { icon: Target, title: t('SDG-Ausrichtung', 'SDG alignment'), description: t('Wir orientieren uns an den UN-Nachhaltigkeitszielen und haben mit ehoch3 eine Analyse unserer relevanten Ziele durchgeführt.', 'We align with the UN Sustainable Development Goals and analysed our relevant goals together with ehoch3.'), status: t('In Umsetzung', 'In progress') },
    { icon: TreePine, title: 'Entrepreneurs For Future', description: t('Als Unterzeichner stehen wir hinter dem Pariser Klimaschutzabkommen und den Forderungen an die Politik.', 'As a signatory we stand behind the Paris Climate Agreement and its demands on policy.'), status: t('Unterzeichnet', 'Signed') },
    { icon: Heart, title: 'Nachhaltig Zusammen', description: t('Wir unterstützen die Initiative für eine solidarische und ökologische Transformation der Wirtschaft.', 'We support the initiative for a solidary and ecological transformation of the economy.'), status: t('Aktiv', 'Active') },
  ];
  const sdgGoals = [
    { number: 7, name: t('Bezahlbare und saubere Energie', 'Affordable and clean energy') },
    { number: 8, name: t('Menschenwürdige Arbeit', 'Decent work') },
    { number: 9, name: t('Industrie, Innovation und Infrastruktur', 'Industry, innovation and infrastructure') },
    { number: 12, name: t('Nachhaltige Produktion', 'Sustainable production') },
    { number: 13, name: t('Maßnahmen zum Klimaschutz', 'Climate action') },
  ];
  return (
    <Layout>
      <SEOHead
        title={t('Nachhaltigkeit – Ökologische Fertigung', 'Sustainability – Eco-conscious manufacturing')}
        description={t('Nachhaltigkeit bei FERNAU: 100% Ökostrom, CO₂-freie Lieferung per Lastenrad, BNW-Mitglied. Verantwortungsvolle CNC-Fertigung in Darmstadt.', 'Sustainability at FERNAU: 100% renewable electricity, CO₂-free cargo-bike delivery, BNW member. Responsible CNC manufacturing in Darmstadt, Germany.')}
        path="/nachhaltigkeit"
        jsonLd={[
          getBreadcrumbJsonLd([{ name: t('Nachhaltigkeit', 'Sustainability'), path: '/nachhaltigkeit' }], lang),
          getFaqJsonLd(
            lang === 'en'
              ? [
                  { question: 'Does FERNAU produce with renewable electricity?', answer: 'Yes, since 2024 FERNAU produces with 100% renewable electricity from hydropower (Lichtblick). The natural gas is ecologically certified as well.' },
                  { question: 'How does FERNAU deliver sustainably?', answer: 'Regional customers are served CO₂-free by cargo bike. Larger shipments use a natural-gas delivery van.' },
                  { question: 'Is FERNAU a member of sustainability networks?', answer: 'Yes, FERNAU has been a member of the German Federal Association for Sustainable Economy (BNW) since 2020, signatory of Entrepreneurs For Future and active in Nachhaltig Zusammen.' },
                ]
              : [
                  { question: 'Produziert FERNAU mit Ökostrom?', answer: 'Ja, FERNAU produziert seit 2024 mit 100 % Ökostrom aus Wasserkraft von Lichtblick. Auch das Erdgas ist ökologisch zertifiziert.' },
                  { question: 'Wie liefert FERNAU nachhaltig?', answer: 'Regionale Kunden werden CO₂-frei per Lastenrad beliefert. Für größere Lieferungen wird ein Erdgas-Lieferwagen eingesetzt.' },
                  { question: 'Ist FERNAU Mitglied in Nachhaltigkeitsnetzwerken?', answer: 'Ja, FERNAU ist seit 2020 Mitglied im Bundesverband Nachhaltige Wirtschaft (BNW), Unterzeichner bei Entrepreneurs For Future und aktiv bei Nachhaltig Zusammen.' },
                ],
            lang,
          ),
        ]}
      />
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.span
            className="text-success font-mono text-sm uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {t('Verantwortung übernehmen', 'Taking responsibility')}
          </motion.span>
          <motion.h1
            className="mt-4 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {t(<>Nachhaltigkeit in der <span className="text-success">Stahlverarbeitung</span></>, <>Sustainability in <span className="text-success">steel manufacturing</span></>)}
          </motion.h1>
          <motion.p
            className="mt-6 text-xl text-muted-foreground max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {t(
              'Wir analysieren unseren CO₂-Ausstoß, Energieverbrauch und die Herkunft der Materialien, um ein besseres Bild unserer Umweltauswirkungen zu erhalten.',
              'We analyse our CO₂ emissions, energy consumption and material origin to gain a clearer picture of our environmental impact.'
            )}
          </motion.p>
        </div>
      </section>

      {/* Intro with Image */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="relative rounded-2xl overflow-hidden"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={sustainabilityImage}
                alt={t('Nachhaltige Produktion', 'Sustainable production')}
                className="w-full aspect-video object-cover"
                draggable={false}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold">{t('Unser Weg zur Nachhaltigkeit', 'Our path to sustainability')}</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                {t(
                  'Was bedeutet Nachhaltigkeit in der Stahlverarbeitung? Dieser Frage gehen wir auf den Grund. Wir zeigen, welche Schritte wir erreicht haben und was uns inspiriert. Wir teilen Links, Aufrufe und Infos, da wir damit auch andere Firmen inspirieren möchten.',
                  'What does sustainability mean in steel manufacturing? We dig into that question. We show which steps we have taken and what inspires us. We share links, calls to action and information to inspire other companies as well.',
                )}
              </p>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                {t(
                  'Für weitere Vorschläge sind wir offen und freuen uns über Anregungen Ihrerseits. Klimaschutz, Solidarität und wirtschaftlicher Erfolg lassen sich nicht gegeneinander aufrechnen!',
                  'We welcome further suggestions and look forward to your input. Climate action, solidarity and economic success cannot be played off against each other!',
                )}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Initiatives Grid */}
      <section className="py-16 bg-card">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">{t('Unsere Initiativen', 'Our initiatives')}</h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              {t('Konkrete Maßnahmen für eine nachhaltigere Produktion', 'Concrete measures for more sustainable production')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {initiatives.map((initiative, index) => (
              <motion.div
                key={initiative.title}
                className="card-industrial p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start justify-between">
                   <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                     <initiative.icon className="w-6 h-6 text-success" />
                   </div>
                   <span className="text-xs font-mono text-success bg-success/10 px-2 py-1 rounded">
                     {initiative.status}
                  </span>
                </div>
                <h3 className="mt-4 text-lg font-semibold">{initiative.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {initiative.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SDG Goals */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold">{t('UN-Nachhaltigkeitsziele', 'UN Sustainable Development Goals')}</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                {t(
                  'Was hat die UN mit uns zu tun? Eine Menge. Auf der Pariser Klimakonferenz wurden 17 Bereiche vereinbart, die wesentlich sind für eine stabile Gesellschaft, weltweit. Wir haben die für uns relevanten Ziele identifiziert und setzen sie konsequent um.',
                  'What does the UN have to do with us? A lot. At the Paris Climate Conference 17 goals were agreed that are essential for a stable global society. We identified the ones relevant to us and pursue them consistently.',
                )}
              </p>
            </div>

            <div className="space-y-3">
              {sdgGoals.map((goal) => (
                <motion.div
                  key={goal.number}
                  className="flex items-center gap-4 p-4 rounded-lg bg-secondary/50 border border-border/30"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center font-bold text-lg text-primary">
                    {goal.number}
                  </div>
                  <div className="flex-1">
                    <span className="text-sm text-muted-foreground">SDG {goal.number}</span>
                    <h4 className="font-medium">{goal.name}</h4>
                  </div>
                  <CheckCircle className="w-5 h-5 text-success" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partner Links */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-center mb-8">{t('Unsere Netzwerke', 'Our networks')}</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { name: 'Bundesverband Nachhaltige Wirtschaft', url: 'https://www.bnw-bundesverband.de/' },
              { name: 'Entrepreneurs For Future', url: 'https://www.entrepreneurs4future.de/' },
              { name: 'Nachhaltig Zusammen', url: 'https://nachhaltig-zusammen.de/' },
            ].map((partner) => (
              <a
                key={partner.name}
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border/50 text-sm hover:border-primary/50 transition-colors"
              >
                {partner.name}
                <ExternalLink className="w-3 h-3" />
              </a>
            ))}
          </div>
        </div>
      </section>

      <FaqSection
        faqs={lang === 'en' ? [
          { question: 'Does FERNAU produce with renewable electricity?', answer: 'Yes, FERNAU produces with 100% renewable electricity from hydropower (Lichtblick). The natural gas is ecologically certified as well.' },
          { question: 'How does FERNAU deliver sustainably?', answer: 'Regional customers are served CO₂-free by cargo bike. Larger shipments use a natural-gas delivery van.' },
          { question: 'Is FERNAU a member of sustainability networks?', answer: 'Yes, FERNAU has been a member of the German Federal Association for Sustainable Economy (BNW) since 2020, a signatory of Entrepreneurs For Future and active in Nachhaltig Zusammen.' },
          { question: 'Which UN Sustainable Development Goals does FERNAU follow?', answer: 'FERNAU aligns with SDGs 7 (affordable and clean energy), 8 (decent work), 9 (industry and innovation), 12 (sustainable production) and 13 (climate action).' },
          { question: 'What does sustainability mean in steel processing?', answer: 'FERNAU analyses CO₂ emissions, energy consumption and material origin to reduce environmental impact while ensuring the highest manufacturing quality.' },
        ] : [
          { question: 'Produziert FERNAU mit Ökostrom?', answer: 'Ja, FERNAU produziert mit 100 % Ökostrom aus Wasserkraft von Lichtblick. Auch das Erdgas ist ökologisch zertifiziert.' },
          { question: 'Wie liefert FERNAU nachhaltig?', answer: 'Regionale Kunden werden CO₂-frei per Lastenrad beliefert. Für größere Lieferungen wird ein Erdgas-Lieferwagen eingesetzt.' },
          { question: 'Ist FERNAU Mitglied in Nachhaltigkeitsnetzwerken?', answer: 'Ja, FERNAU ist seit 2020 Mitglied im Bundesverband Nachhaltige Wirtschaft (BNW), Unterzeichner bei Entrepreneurs For Future und aktiv bei Nachhaltig Zusammen.' },
          { question: 'An welchen UN-Nachhaltigkeitszielen orientiert sich FERNAU?', answer: 'FERNAU orientiert sich an den SDGs 7 (Bezahlbare und saubere Energie), 8 (Menschenwürdige Arbeit), 9 (Industrie und Innovation), 12 (Nachhaltige Produktion) und 13 (Klimaschutz).' },
          { question: 'Was bedeutet Nachhaltigkeit in der Stahlverarbeitung?', answer: 'FERNAU analysiert den CO₂-Ausstoß, den Energieverbrauch und die Herkunft der Materialien, um die Umweltauswirkungen zu reduzieren und gleichzeitig höchste Fertigungsqualität zu gewährleisten.' },
        ]}
      />

      <CTASection />
    </Layout>
  );
}
